/*
 * How core's own activity kinds read in the feed, and where they lead — the same seam a module
 * fills for its kinds (`FrontendModule.activityRows`, S-11-D7), for the kinds core registers
 * itself: the two operator items of the worker gateway — the machine that runs the AI has not
 * answered for a while, and it answers again; both go to administrators alone, and both lead to the
 * AI worker page, which is where the reason is — and the rows a document writes, which
 * `docRows.ts` reads.
 *
 * Nothing is invented: every sentence is a key of the catalogue beside this file, and a payload
 * without the number the sentence names produces "" and lets core fall back to the kind's own
 * label rather than putting a brace in front of a reader.
 */

import { t } from "../i18n/index.ts"
import type { ActivityRowView } from "../modules/registry.ts"
import { DOC_ACTIVITY_ROWS } from "./docRows.ts"

/** The payload the server writes with both items (`internal/core/work/reap.go`, `absenceItem`). */
interface AbsenceItem {
	worker?: string
	minutes?: number
}

/** Where both rows lead: the operator's page about the worker. */
const WORK_PAGE = "/admin/work"

function read(data: unknown): AbsenceItem {
	return typeof data === "object" && data !== null ? data : {}
}

/** A core kind's reading: the module seam's answers, its label always given. */
export interface CoreRowView extends ActivityRowView {
	/** What kind of thing this is, as the sentence's own key names it: the name of its read button. */
	label: () => string
	/**
	 * Whether this row of a kind that banners is one to banner (`rowView.ts`): a document's mention
	 * does unless Claude wrote it, a reply only on the thread starter's row. Absent, every row does.
	 */
	banners?: (data: unknown) => boolean
}

const unavailable: CoreRowView = {
	label: () => t("activity.core.work.unavailable.label"),
	line(data) {
		const item = read(data)
		if (typeof item.minutes !== "number") return ""
		return `${t("activity.core.work.unavailable.title", { minutes: item.minutes })}. ${t("activity.core.work.unavailable.body")}`
	},
	href: () => WORK_PAGE,
}

const back: CoreRowView = {
	label: () => t("activity.core.work.back.label"),
	line: () => t("activity.core.work.back.title"),
	href: () => WORK_PAGE,
}

/** kind → how core reads its own row; undefined for every kind core has no view of. */
const CORE_ROWS: Readonly<Record<string, CoreRowView>> = {
	"core.work.unavailable": unavailable,
	"core.work.back": back,
	...DOC_ACTIVITY_ROWS,
}

export function coreActivityRow(kind: string): CoreRowView | undefined {
	return CORE_ROWS[kind]
}
